import { ProductSummary } from "@/common/productSummary/types";
import { getUserInfoFromJWT } from "@/lib/auth/getUserInfo";

export interface PersistedGuidedPathData {
  answers: Record<string, string | string[]>;
  results: ProductSummary[];
  timestamp: number;
}

const GUIDED_PATH_STORAGE_KEY = "guided-path-results";

const resolveGuidedPathStorageKey = (): string | null => {
  if (typeof window === "undefined") {
    return null;
  }

  const userInfo = getUserInfoFromJWT();
  const identifier = userInfo?.user_id ?? userInfo?.sub;

  if (!identifier) {
    return null;
  }

  return `${GUIDED_PATH_STORAGE_KEY}-${identifier}`;
};

export const persistGuidedPathResults = (data: PersistedGuidedPathData) => {
  const storageKey = resolveGuidedPathStorageKey();
  if (!storageKey || typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(storageKey, JSON.stringify(data));
  } catch (error) {
    console.error("Unable to persist guided path results", error);
  }
};

export const clearGuidedPathResults = () => {
  const storageKey = resolveGuidedPathStorageKey();
  if (!storageKey || typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.removeItem(storageKey);
  } catch (error) {
    console.error("Unable to clear guided path results", error);
  }
};

export const loadPersistedGuidedPathResults =
  (): PersistedGuidedPathData | null => {
    const storageKey = resolveGuidedPathStorageKey();
    if (!storageKey || typeof window === "undefined") {
      return null;
    }

    try {
      const storedValue = window.localStorage.getItem(storageKey);
      if (!storedValue) {
        return null;
      }

      return JSON.parse(storedValue) as PersistedGuidedPathData;
    } catch (error) {
      console.error("Unable to restore guided path results", error);
      return null;
    }
  };
